<template>
  <div>
    <div class="col-12 px-0">
      <h1 class="d-flex align-items-center">
        <span class="icon mr-2 icon-kalender" aria-hidden="true"></span>
        {{ $t('views.timeSlotList.title' ) }}
      </h1>
    </div>

    <link-back to="/rooms" i18nMessage="common.backToOverview" />

    <h2 class="my-2">{{ room.name }}</h2>
    <p class="mb-2 mt-0">{{ room.description }}</p>

    <loading-container :state="state">
      <link-add-element i18nMessage="common.timeSlot.add" :to="{ name: 'admin-time-slot-add', params: { id: room.id }, query: { 'fromSearch': 'y'} }" class="d-flex justify-content-end mb-2" />
      <div class="d-flex justify-content-end">
        <router-link
            class="d-flex align-items-center no-underline text-positive"
            :to="{
            name: 'admin-time-slot-edit-multi-capacity',
            params: { 'roomId': roomId },
            query: { 'fromSearch': 'y' }
          }">
          <span class="icon mr-2 icon-editieren" aria-hidden="true"></span>
          <span>{{ $t('views.timeSlotList.multiEditCapacity') }}</span>
        </router-link>
      </div>
      <calendar-month-selector :calendarForDay="calendarForDay" v-on:changed="changeMonth($event)" class="mt-3" />
      <calendar-monthly :room="room" :days="days" v-on:clicked="onTimeSlotClicked($event)" :adminDashboard="true" />
    </loading-container>
  </div>
</template>

<script lang="ts">
import CalendarMonthly from '@/components/calendar/CalendarMonthly.vue';
import CalendarMonthSelector from '@/components/calendar/CalendarMonthSelector.vue';
import LinkAddElement from '@/components/interaction/LinkAddElement.vue';
import LinkBack from '@/components/interaction/LinkBack.vue';
import LoadingContainer from '@/components/structure/LoadingContainer.vue';
import { Room, TimeSlot } from '@/generated/openapi';
import LoadingMixin from '@/mixins/LoadingMixin';
import { CalendarDay } from '@/models/Calendar';
import { adminService } from '@/services/adminService';
import { asDays } from '@/utils/calendarUtils';
import { now, withDay } from '@/utils/dateTimeUtils';
import { LoadingState } from '@/utils/loadingUtils';
import { urlParamString, urlQueryInt } from '@/utils/navigationUtils';
import { DateTime } from 'luxon';
import { defineComponent } from 'vue';


export default defineComponent({
  name: 'TimeSlotList',
  mixins: [
    LoadingMixin
  ],
  data() {
    return {
      roomId: '',
      room: {} as Room,
      timeSlots: [] as TimeSlot[],
      days: [] as CalendarDay[],
      calendarForDay: now()
    }
  },
  components: {
    LoadingContainer,
    LinkAddElement,
    CalendarMonthly,
    LinkBack,
    CalendarMonthSelector,
  },
  created() {
    this.roomId = urlParamString(this.$route, 'id')
    this.historyToState()
    this.loadFromServer()
  },
  methods: {
    onTimeSlotClicked([roomId, timeSlotId]: [string, string]) {
      this.$router.push({
        name: 'admin-time-slot-edit',
        params: {
          roomId: roomId,
          timeSlotId: timeSlotId
        },
        query: {
          fromSearch: 'y'
        }
      })
    },
    changeMonth(newDate: DateTime) {
      this.calendarForDay = newDate
      this.stateToHistory();
      this.loadFromServer()
    },
    loadFromServer() {
      this.state = LoadingState.loading
      Promise.all([
        adminService.getRoom(this.roomId),
        adminService.findTimeSlots(this.roomId, withDay(this.calendarForDay, 1).toISODate(), withDay(this.calendarForDay, this.calendarForDay.daysInMonth).toISODate())
      ]).then(([room, timeslots]) => {
        this.room = room
        this.timeSlots = timeslots.content || []
        this.days = asDays(this.timeSlots, this.calendarForDay)
        this.state = LoadingState.success
      }).catch(this.handleError)
    },
    historyToState() {
      const month = urlQueryInt(this.$route, 'month')
      const year = urlQueryInt(this.$route, 'year')
      if (month && year && Number.isInteger(month) && month > 0 && month < 13 && Number.isInteger(year)) {
        this.calendarForDay = DateTime.fromObject({
          year,
          month,
          day: 1
        })
      }
    },
    stateToHistory() {
      this.$router.push({
        path: this.$route.path,
        query: {
          month: this.calendarForDay.month,
          year: this.calendarForDay.year
        }
      })
    }
  }
});
</script>
